import { AuthorizationRelations } from '@/types/authorization'
import { ResourceType } from '@/Network/Types/Responses/PermissionResponses'

/**
 * Fonte canônica dos labels pt-BR de relations de autorização e de resource
 * types. Substitui as cópias duplicadas em share.modal.vue,
 * PermissionsAccordionItem.vue e PermissionsTreeModal (migração dos
 * consumidores em follow-up).
 *
 * Chaveado pelo enum `AuthorizationRelations` de propósito: relation nova sem
 * label vira erro de compilação aqui, não fallback silencioso na UI.
 */
export const AUTHORIZATION_RELATION_LABELS: Record<AuthorizationRelations, string> = {
  [AuthorizationRelations.Platform]: 'Plataforma',
  [AuthorizationRelations.Parent]: 'Proprietário',
  [AuthorizationRelations.GeneralAdmin]: 'Administrador Geral',
  [AuthorizationRelations.ContentAdmin]: 'Administrador de Conteúdo',
  [AuthorizationRelations.IntermediateEditor]: 'Editor Intermediário',
  [AuthorizationRelations.BasicEditor]: 'Editor Básico',
  [AuthorizationRelations.AdvancedReader]: 'Leitor Avançado',
  [AuthorizationRelations.IntermediateReader]: 'Leitor Intermediário',
  [AuthorizationRelations.BasicReader]: 'Leitor Básico',
  [AuthorizationRelations.DirectMember]: 'Membro Direto',
  [AuthorizationRelations.Member]: 'Membro',
  [AuthorizationRelations.Emitter]: 'Emissor',
  [AuthorizationRelations.BasicReceiver]: 'Receptor Básico',
  [AuthorizationRelations.AdvancedReceiver]: 'Receptor Avançado',
  [AuthorizationRelations.Extractor]: 'Extrator',
  [AuthorizationRelations.QualityAdmin]: 'Configurador',
  [AuthorizationRelations.TechnicalEvaluator]: 'Avaliador Técnico',
  [AuthorizationRelations.InstitutionalEvaluator]: 'Avaliador Institucional',
  [AuthorizationRelations.AnalyticsViewer]: 'Analista de Resultados',
  [AuthorizationRelations.Operator]: 'Operação',
  [AuthorizationRelations.MedicalCoordinator]: 'Tático',
  [AuthorizationRelations.AuditAnalyst]: 'Análise de Risco',
  [AuthorizationRelations.TechnicalValidator]: 'Técnica',
  [AuthorizationRelations.StrategicValidator]: 'Estratégica',
  [AuthorizationRelations.ExecutiveValidator]: 'Executiva',
  [AuthorizationRelations.DocumentationAnalyst]: 'Analista de Documentação',
  [AuthorizationRelations.FinancialAnalyst]: 'Analista Financeiro'
}

/**
 * Label pt-BR de uma relation. Relation desconhecida vira texto legível
 * (underscores → espaços); ausente vira 'Indefinido'.
 */
export const getRelationLabel = (relation?: string | null): string => {
  if (!relation) return 'Indefinido'
  return (
    AUTHORIZATION_RELATION_LABELS[relation as AuthorizationRelations] ?? relation.replace(/_/g, ' ')
  )
}

/**
 * Labels pt-BR dos resource types exibidos na UI de permissões. `Partial` de
 * propósito: nem todo `ResourceType` aparece na UI; os não mapeados caem no
 * fallback de `getResourceTypeLabel`.
 */
export const RESOURCE_TYPE_LABELS: Partial<Record<ResourceType, string>> = {
  [ResourceType.Company]: 'Empresa',
  [ResourceType.HealthUnit]: 'Unidade',
  [ResourceType.Schedule]: 'Escala',
  [ResourceType.Group]: 'Grupo',
  [ResourceType.User]: 'Usuário',
  [ResourceType.Platform]: 'Plataforma'
}

/**
 * Label pt-BR de um resource type. Tipo desconhecido vira o próprio valor em
 * caixa alta; ausente vira 'N/A'.
 */
export const getResourceTypeLabel = (type?: string | null): string => {
  if (!type) return 'N/A'
  return RESOURCE_TYPE_LABELS[type as ResourceType] ?? type.toUpperCase()
}

/** Cor Vuetify do chip de um resource type na UI de permissões. */
export const getResourceTypeColor = (type?: string | null): string => {
  if (type === ResourceType.Company) return 'primary'
  if (type === ResourceType.HealthUnit) return 'teal'
  if (type === ResourceType.Schedule) return 'blue-grey'
  return 'grey'
}
